import { useEffect, useState, useCallback } from "react";
import { X, ArrowRight, ArrowLeft, Compass, Database } from "lucide-react";

const STEPS = [
  {
    title: "Welcome to UniVicoustic CRM",
    body: "A 90-second tour of the platform for management & sales heads — what each module does and where its data comes from.",
    source: "Built on your day-to-day CRM activity + your imported Sales MIS / master data.",
  },
  {
    selector: "nav-dashboard",
    title: "Dashboard",
    body: "Your command center — live KPIs, target progress, pipeline funnel and follow-up reminders at a glance.",
    source: "Computed live from your CRM (leads, deals, targets, activities).",
  },
  {
    selector: "nav-leads",
    title: "Leads",
    body: "Capture & qualify enquiries with SLA timers and round-robin dealer assignment. Each lead can be tagged to a sub-brand (Univic / PET Panel).",
    source: "Entered by sales reps & dealers; deal-registration protects the first dealer for 30 days.",
  },
  {
    selector: "nav-deals",
    title: "Deals (Pipeline)",
    body: "Drag deals across stages (Kanban) or work the table view. Filter by sub-brand and rating.",
    source: "CRM opportunities — generated from qualified leads.",
  },
  {
    selector: "nav-quotes",
    title: "Quotes / BOQ",
    body: "Build quotations & BOQs with auto-approval thresholds so small quotes clear instantly and large ones route for sign-off.",
    source: "CRM — generated against deals using your product catalog & discount rules.",
  },
  {
    selector: "nav-samples",
    title: "Samples",
    body: "Track free/paid sample requests and their fulfilment to dealers and architects.",
    source: "CRM — raised by reps/dealers, fulfilled by the factory.",
  },
  {
    selector: "nav-reports",
    title: "Reports",
    body: "Win/loss, pending orders, collections — plus the Zone / Branch sales summary (booking vs won) drillable into branches.",
    source: "Computed live from CRM deals; zones/branches come from the dealer master (mis.csv).",
  },
  {
    selector: "nav-sales-mis",
    title: "Sales MIS (Management)",
    body: "The MIS hub: Sales Dashboard (toggle Excel snapshot ↔ Live-from-CRM), the weekly DCR, and all reference sheets (Large Deals, BD Leads, regional Dealer Dashboards, Market Sizing). Admins can refresh it monthly via 'Update Sales MIS'.",
    source: "Snapshot & reference sheets imported from Sales MIS.xlsx; the Live view is computed in real time from CRM.",
  },
  {
    title: "You're all set 🎉",
    body: "Data flows two ways: your Excel MIS (Sales MIS, master/dealer data) was imported as a baseline, and everything your team logs daily keeps the Live dashboards current. Re-launch this tour anytime from the top bar.",
    source: "Tip: keep your numbers fresh — upload the latest workbook each month from Sales MIS → Update Sales MIS.",
  },
];

export function ProductTour({ open, onClose }) {
  const [steps, setSteps] = useState([]);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const check = () => setDialogOpen(!!document.querySelector('[role="dialog"][data-state="open"]'));
    check();
    const t = setInterval(check, 800);
    return () => clearInterval(t);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const avail = STEPS.filter((s) => !s.selector || document.querySelector(`[data-testid="${s.selector}"]`));
    setSteps(avail);
    setI(0);
  }, [open]);

  const measure = useCallback(() => {
    const step = steps[i];
    if (!step || !step.selector) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.selector}"]`);
    if (el) {
      el.scrollIntoView({ block: "nearest" });
      setRect(el.getBoundingClientRect());
    } else {
      setRect(null);
    }
  }, [steps, i]);

  useEffect(() => {
    if (!open) return;
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [open, measure]);

  if (!open || steps.length === 0 || dialogOpen) return null;

  const step = steps[i];
  const last = i === steps.length - 1;
  const pad = 6;
  const box = rect && { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 };

  let cardStyle;
  if (rect) {
    const cardW = 340;
    const fitsRight = rect.right + 16 + cardW < window.innerWidth;
    cardStyle = {
      left: fitsRight ? rect.right + 16 : Math.max(16, rect.left),
      top: Math.min(Math.max(rect.top, 16), window.innerHeight - 290),
      width: cardW,
    };
  } else {
    cardStyle = { left: "50%", top: "50%", transform: "translate(-50%, -50%)", width: 440 };
  }

  const finish = () => { onClose?.(); };
  const next = () => (last ? finish() : setI((v) => v + 1));
  const back = () => setI((v) => Math.max(0, v - 1));

  return (
    <div className="fixed inset-0 z-[120]" data-testid="product-tour">
      {/* dim + spotlight */}
      {box ? (
        <div className="absolute rounded-md border-2 border-brand transition-all duration-300 pointer-events-none"
          style={{ top: box.top, left: box.left, width: box.width, height: box.height, boxShadow: "0 0 0 9999px rgba(15,23,42,0.62)" }} />
      ) : (
        <div className="absolute inset-0" style={{ backgroundColor: "rgba(15,23,42,0.62)" }} />
      )}

      {/* tooltip card */}
      <div className="absolute bg-white rounded-xl shadow-2xl border border-slate-200 p-5 fade-in" style={cardStyle} data-testid="tour-card">
        <div className="flex items-start justify-between gap-3 mb-2">
          <div className="flex items-center gap-2">
            <span className="h-7 w-7 rounded-lg bg-[#FFEAE7] text-brand flex items-center justify-center flex-shrink-0"><Compass className="h-4 w-4" /></span>
            <h3 className="font-heading font-bold text-slate-900 text-base leading-tight">{step.title}</h3>
          </div>
          <button onClick={finish} className="text-slate-300 hover:text-slate-600 flex-shrink-0" data-testid="tour-close"><X className="h-4 w-4" /></button>
        </div>

        <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>

        {step.source && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2">
            <Database className="h-3.5 w-3.5 text-slate-400 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-slate-500 leading-snug"><span className="font-semibold text-slate-600">Data source: </span>{step.source}</p>
          </div>
        )}

        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-1.5">
            {steps.map((_, idx) => (
              <span key={idx} className={`h-1.5 rounded-full transition-all ${idx === i ? "w-5 bg-brand" : "w-1.5 bg-slate-200"}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {i > 0 && (
              <button onClick={back} className="h-8 px-3 rounded-md text-sm font-medium text-slate-600 hover:bg-slate-100 inline-flex items-center gap-1" data-testid="tour-back">
                <ArrowLeft className="h-3.5 w-3.5" /> Back
              </button>
            )}
            <button onClick={next} className="h-8 px-4 rounded-md text-sm font-semibold bg-slate-900 text-white hover:bg-slate-800 inline-flex items-center gap-1.5" data-testid="tour-next">
              {last ? "Finish" : "Next"} {!last && <ArrowRight className="h-3.5 w-3.5" />}
            </button>
          </div>
        </div>

        {!last && (
          <button onClick={finish} className="mt-2 text-[11px] text-slate-400 hover:text-slate-600 w-full text-center" data-testid="tour-skip">Skip tour</button>
        )}
      </div>
    </div>
  );
}
